"use client";
import {
  useDeleteDriverDetails,
  useGetAllStates,
  useGetCityDataMutation,
  useGetDriverLicenseInfo,
  useGetStates,
  useGetUserKycList,
  useGetUserProfileInfo,
  useUpdateLicenseDetails,
  useUpdateUserDoc,
  useUpdateUserProfilePhoto,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Camera,
  CheckCircle,
  IdCard,
  MapPin,
  Phone,
  Shield,
  UploadCloud,
} from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormData, schema } from "./Profile";
import { toast } from "react-toastify";
import { useCity } from "@/store/common";

import { z } from "zod";
import ImageEditor from "./ImageEditor";

const licenseSchema = z.object({
  license_state: z.string().min(1, "License State is required"),
  license_state_code: z.number().optional(),
  license_validity: z.string().min(1, "License Validity is required."),
  pancard: z.string().optional(),
  license_no: z.string().min(1, "Badge/License No. is required."),
  gps_device_no: z.string().optional(),
  license_proof_path: z.string().optional(),
  police_report_path: z.string().optional(),
  pancard_proof_path: z.string().optional(),
  audit_report_path: z.string().optional(),
  bank_proof_path: z.string().optional(),
});

export type licenseData = z.infer<typeof licenseSchema>;

export default function UserLicenseDetailForm() {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<licenseData>({
    resolver: zodResolver(licenseSchema),
  });

  const [cityInput, setCityInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [selectedCityId, setSelectedCityId] = useState<string>("");

  const searchParams = useSearchParams();
  const user_id = searchParams.get("user_id");
  const path = searchParams.get("type");
  const { user: authUser } = useAuth();
  const userId = path ? user_id : authUser?.id ?? -1;

  const {
    data: userProfile,
    isLoading: isProfileLoading,
    isError,
    error,
  } = useGetUserProfileInfo(Number(userId));

  const [debouncedGetCity, setDebouncedGetCity] = useState(() => () => {});
  const [licenseProof, setLicenseProof] = useState<string | null>();
  const [policeReport, setPoliceReport] = useState<string | null>();
  const [pancardProof, setPancardProof] = useState<string | null>();
  const [auditReport, setAuditReport] = useState<string | null>();
  const [bankProof, setBankProof] = useState<string | null>();
  const [filteredStates, setFilteredStates] = useState([]);
  const [dropdown, setDropdown] = useState<number | null>(null);

  const watchStateName = watch("license_state");

  const { mutate: uploadPhoto } = useUpdateUserProfilePhoto(Number(userId));

  const { mutate: deleteDriver } = useDeleteDriverDetails();
  const { data: driverData, refetch: refetchDriverData } =
    useGetDriverLicenseInfo();


  useEffect(() => {
    refetchDriverData();
  }, []);

  useEffect(() => {
    if (driverData) {
      setValue("license_state", String(driverData?.driver?.name));
      setValue("license_validity", driverData?.driver?.license_validity);
      setValue("pancard", driverData?.driver?.pancard_no);
      setValue("license_no", driverData?.driver?.driving_license_no);
      setValue("gps_device_no", driverData?.driver?.gps);
      setLicenseProof(`${driverData?.documents?.license_proof}`);
      setPoliceReport(`${driverData?.documents?.police_proof}`);
      setPancardProof(`${driverData?.documents?.pancard_proof}`);
      setAuditReport(`${driverData?.documents?.audit_proof}`);
      setBankProof(`${driverData?.documents?.bank_proof}`);
    }
  }, [driverData, refetchDriverData]);

  // const { data: statesData } = useGetStates("");

  const { data: allStatesData } = useGetAllStates();

  const { mutate: uploadDoc, data: uploadedData } = useUpdateUserDoc(
    Number(userId)
  );
  const { data: kycData, isLoading } = useGetUserKycList("kyc_proof");

  const b2bUrl = process.env.NEXT_PUBLIC_B2B_URL;

  const { mutate: getCityData } = useGetCityDataMutation();
  const { cityData } = useCity();

  const kycList = useMemo(() => {
    return kycData || [];
  }, [kycData]);

  const handleCityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCityInput(value);
    setShowSuggestions(true);
    debouncedGetCity();
  };

  // const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       console.log(file);
  //       return;
  //       setProfilePreview(reader.result as string);
  //     };
  //     reader.readAsDataURL(file);
  //     uploadPhoto({ file, folder: "profile" });
  //   }
  // };
  const handleDeleteBtn = () => {
    if (confirm("Are you sure you want to delete this driver?")) {
      deleteDriver(undefined, {
        onSuccess: () => {
          toast.success("Driver deleted successfully!");
        },
        onError: () => {
          toast.error("Failed to delete driver.");
        },
      });
    }
  };

  const onCompleteBankProof = () =>{
    console.log("Submitted")
  }

  const handleLicenseProofChange = (e: any, typeId: any) => {
    const file = e.target.files?.[0];
    // console.log(file);
    if (file) {
      uploadDoc(
        {
          file,
          folder: "profile",
          doc_type_id: typeId,
          user_id: String(userId) || "",
        },
        {
          onSuccess: (response: any) => {
            // console.log("response: ", response?.uploadData?.doc_file_upload);
            setLicenseProof(response?.uploadData?.doc_file_upload);
            console.log(response?.uploadData?.doc_file_upload);
            setValue(
              "license_proof_path",
              String(response?.uploadData?.doc_file_upload) || undefined
            );
          },
          onError: (err: any) => {
            toast.error("Something went wrong. Please try again.");
          },
        }
      );
    }
  };

  const handlePoliceReportChange = (e: any, typeId: any) => {
    const file = e.target.files?.[0];
    // console.log(file);
    if (file) {
      uploadDoc(
        {
          file,
          folder: "profile",
          doc_type_id: typeId,
          user_id: String(userId) || "",
        },
        {
          onSuccess: (response: any) => {
            // console.log("response: ", response?.uploadData?.doc_file_upload);
            setPoliceReport(response?.uploadData?.doc_file_upload);
            setValue(
              "police_report_path",
              String(response?.uploadData?.doc_file_upload) || undefined
            );
          },
          onError: (err: any) => {
            toast.error("Something went wrong. Please try again.");
          },
        }
      );
    }
  };

  const handlePanProofChange = (e: any, typeId: any) => {
    const file = e.target.files?.[0];
    // console.log(file);
    if (file) {
      uploadDoc(
        {
          file,
          folder: "profile",
          doc_type_id: typeId,
          user_id: String(userId) || "",
        },
        {
          onSuccess: (response: any) => {
            // console.log("response: ", response?.uploadData?.doc_file_upload);
            setPancardProof(response?.uploadData?.doc_file_upload);
            setValue(
              "pancard_proof_path",
              String(response?.uploadData?.doc_file_upload) || undefined
            );
          },
          onError: (err: any) => {
            toast.error("Something went wrong. Please try again.");
          },
        }
      );
    }
  };

  const handleAuditReportChange = (e: any, typeId: any) => {
    const file = e.target.files?.[0];
    // console.log(file);
    if (file) {
      uploadDoc(
        {
          file,
          folder: "profile",
          doc_type_id: typeId,
          user_id: String(userId) || "",
        },
        {
          onSuccess: (response: any) => {
            // console.log("response: ", response?.uploadData?.doc_file_upload);
            setAuditReport(response?.uploadData?.doc_file_upload);
            setValue(
              "audit_report_path",
              String(response?.uploadData?.doc_file_upload) || undefined
            );
          },
          onError: (err: any) => {
            toast.error("Something went wrong. Please try again.");
          },
        }
      );
    }
  };

  const handleBankProofChange = (e: any, typeId: any) => {
    const file = e.target.files?.[0];
    // console.log(file);
    if (file) {
      uploadDoc(
        {
          file,
          folder: "profile",
          doc_type_id: typeId,
          user_id: String(userId) || "",
        },
        {
          onSuccess: (response: any) => {
            // console.log("response: ", response?.uploadData?.doc_file_upload);
            setBankProof(response?.uploadData?.doc_file_upload);
            setValue(
              "bank_proof_path",
              String(response?.uploadData?.doc_file_upload) || undefined
            );
          },
          onError: (err: any) => {
            toast.error("Something went wrong. Please try again.");
          },
        }
      );
    }
  };

  useEffect(() => {
    if (
      typeof watchStateName !== "string" ||
      watchStateName.trim().length < 3
    ) {
      setDropdown(1);
      setFilteredStates([]);
      return;
    }

    const filtered = allStatesData?.filter((state: any) =>
      state.name.toLowerCase().startsWith(watchStateName.toLowerCase())
    );
    setFilteredStates(filtered);
  }, [watchStateName, allStatesData]);

  const handleSelect = (state: any) => {
    if (state) {
      // alert(state?.id);
      setValue("license_state", state?.name);
      setValue("license_state_code", state?.id);
      setDropdown(null);
    }
    setFilteredStates([]);
  };

  const updateMutation = useUpdateLicenseDetails();
  const profilePath = process.env.NEXT_PUBLIC_API_PIC_URL;

  const fileParts = userProfile?.user_profile_path?.split("/");
  const folderPath = fileParts?.slice(0, -1).join("/");
  const fileName =
    fileParts !== undefined ? fileParts[fileParts.length - 1] : "";

  const profilePicture = `${profilePath}${folderPath}/${encodeURIComponent(
    fileName
  )}`;

  // const onSubmit = async (data: licenseData) => {
  //   const payload = {
  //     ...data,
  //     license_proof_path: licenseProof,
  //     police_report_path: policeReport,
  //     pancard_proof_path: pancardProof,
  //     audit_report_path: auditReport,
  //     bank_proof_path: bankProof,
  //   }
  //   console.log(payload);

  //   updateMutation.mutate(data);
  // };

  const onSubmit = async (data: licenseData) => {
    const payload = {
      // ...data,
      license_state: data?.license_state_code,
      license_validity: data?.license_validity,
      gps: data?.gps_device_no,
      driving_license_no: data?.license_no,
      pancard_no: data?.pancard,
      license_proof: licenseProof || userProfile.license_proof_path,
      police_proof: policeReport || userProfile.police_report_path,
      pancard_proof: pancardProof || userProfile.pancard_proof_path,
      audit_proof: auditReport || userProfile.audit_report_path,
      bank_proof: bankProof || userProfile.bank_proof_path,
    };

    updateMutation.mutate(payload, {
      onSuccess: () => {
        toast.success("License details updated successfully!");
      },
      onError: () => {
        toast.error("Failed to update profile");
      },
    });
  };

  useEffect(() => {
    console.log(errors);
  }, [errors]);

  return (
    <form
      action=""
      onSubmit={handleSubmit(onSubmit)}
      className="w-full bg-white"
    >
      <div className="flex justify-between">
        <div className="bg-white/70 backdrop-blur-sm border border-white/20 overflow-hidden w-[65%] sm:w-[70%]">
          <div className="p-8 space-y-8">
            <div className="space-y-3">
              <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg">
                  <IdCard className="w-3 h-3 text-white" />
                </div>
                <h3 className="text-[16px] font-semibold text-gray-800">
                  License Detail
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    License State <span className="text-red-600">*</span>
                  </label>
                  <input
                    {...register("license_state")}
                    placeholder="Select State"
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                  />
                  {allStatesData?.map((state: any) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                  {errors.license_state && (
                    <p className="text-[10px] text-red-500">
                      {errors.license_state.message}
                    </p>
                  )}
                </div> */}
                <div className="relative space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    License State <span className="text-red-600">*</span>
                  </label>

                  <input
                    {...register("license_state")}
                    placeholder="Select State"
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                    autoComplete="off"
                  />

                  {dropdown && filteredStates?.length > 0 && (
                    <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-sm max-h-40 overflow-y-auto text-[12px]">
                      {filteredStates?.map((state: any) => (
                        <li
                          key={state.id}
                          className="px-3 py-1 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleSelect(state)}
                        >
                          {state.name}
                        </li>
                      ))}
                    </ul>
                  )}

                  {errors.license_state && (
                    <p className="text-[10px] text-red-500">
                      {errors.license_state.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    License Validity <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    {...register("license_validity")}
                    value={userProfile?.license_validity}
                    placeholder="Enter License Validity"
                    // disabled
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                  />
                  {errors.license_validity && (
                    <p className="text-[10px] text-red-500">
                      {errors.license_validity.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    Pancard
                  </label>
                  <input
                    {...register("pancard")}
                    placeholder="Enter Pancard"
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                  />
                  {/* {errors?.pancard && (
                    <p className="text-[10px] text-red-500">
                      {errors.pancard.message}
                    </p>
                  )} */}
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    Badge/License No. <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("license_no")}
                    placeholder="Enter Badge/License No."
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                  />
                  {errors.license_no && (
                    <p className="text-[10px] text-red-500">
                      {errors.license_no.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    GPS Device No
                  </label>
                  <input
                    {...register("gps_device_no")}
                    placeholder="Enter GPS Device No."
                    className="border border-gray-300 p-2 px-3 outline-none rounded-sm w-full text-[12px] sm:text-[10px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="img-upload px-9 py-12 w-[30%]">
          <div className="flex flex-col items-end gap-6">
            {/* License Proof */}
            {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={(e) => handleLicenseProofChange(e, "7")}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {licenseProof ? (
                      <img
                        src={`${b2bUrl}${licenseProof}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="License Proof"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">
                  License Proof
                </p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}


            <ImageEditor image={licenseProof ?? ""} setImage={setLicenseProof} docTypeId={7} label="License Proof" />


            {/* Police Report */}
            {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    // onChange={handleImageChange}
                    onChange={(e) => handlePoliceReportChange(e, "6")}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {policeReport ? (
                      <img
                        src={`${b2bUrl}${policeReport}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="Police Report"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">
                  Police Report
                </p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}

            <ImageEditor image={policeReport ?? ""} setImage={setPoliceReport} docTypeId={6} 
            label="Police Report" 
            />


            {/* Pan Card Proof */}
            {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    // onChange={handleImageChange}
                    onChange={(e) => handlePanProofChange(e, "3")}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {pancardProof ? (
                      <img
                        src={`${b2bUrl}${pancardProof}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="Pan Card Proof"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">
                  Pan Card Proof
                </p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}

            <ImageEditor image={pancardProof ?? ""} setImage={setPancardProof} docTypeId={3} 
            label="PAN Card Proof" 
            />


            {/* Audit Report */}
            {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    // onChange={handleImageChange}
                    onChange={(e) => handleAuditReportChange(e, "5")}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {auditReport ? (
                      <img
                        src={`${b2bUrl}${auditReport}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="Audit Report"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">
                  Audit Report
                </p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}

            <ImageEditor image={auditReport ?? ""} setImage={setAuditReport} docTypeId={5} 
            label="Audit Report" 
            />

            {/* Bank Proof */}
            {/* <div className="flex flex-col items-center space-y-3">
              <div className="relative group">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    // onChange={handleImageChange}
                    onChange={(e) => handleBankProofChange(e, "28")}
                    className="hidden"
                    accept="image/*"
                  />
                  <div className="relative">
                    {bankProof ? (
                      <img
                        src={`${b2bUrl}${bankProof}`}
                        className="w-35 h-30 object-cover border-4 border-white shadow-md transition-all duration-300"
                        alt="Bank Proof"
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                        <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                      <Camera className="w-4 h-4" />
                    </div>
                  </div>
                </label>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium text-gray-700">
                  Bank Proof
                </p>
                <p className="text-xs text-gray-500">
                  Click to upload or change
                </p>
              </div>
            </div> */}

            <ImageEditor image={bankProof ?? ""} setImage={setBankProof} docTypeId={28}
            label="Bank Proof" 
            />

           
          </div>
        </div>
      </div>
      {/* Form Actions */}
      <div className="flex justify-end gap-4 p-4 border-t border-gray-200">
        <button
          type="button"
          className="py-1 px-3 border border-gray-300 rounded-md shadow-sm text-[10px] font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer"
        >
          Reset
        </button>
        <button
          type="submit"
          className="py-1 px-3 cursor-pointer border border-transparent rounded-md shadow-md text-[10px] font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
        >
          Save
        </button>

        <button
          type="button"
          onClick={handleDeleteBtn}
          className="py-1 px-3 cursor-pointer border border-transparent rounded-md shadow-md text-[10px] font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
        >
          Delete
        </button>
      </div>
    </form>
  );
}
